import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { getPaymentMethods } from '../api/payment-api';
import { getCookie } from '../utils/cookies';

const BalanceContext = createContext();

export const useBalance = () => useContext(BalanceContext);

export const BalanceProvider = ({ children }) => {
  const [isLoggedIn] = useState(!!localStorage.getItem('access_token'));
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(undefined);
  const [currency, setCurrency] = useState(getCookie('currency'));
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(true);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    if (!isLoggedIn) return;
    setIsLoadingPaymentMethods(true);
    setError('');
    getPaymentMethods()
      .then((res) => {
        const data = res.data;
        const formattedMethods = data.map((row) => ({
          value: row['variant'],
          title: row['description'],
          percent: row['service_percent'],
          fixed: row['service_fixed'],
          currency: row['currency'],
        }));
        setPaymentMethods(formattedMethods);

        if (formattedMethods.length > 0) {
          setSelectedVariant(formattedMethods[0]);
          setCurrency(formattedMethods[0].currency);
        } else {
          setSelectedVariant(undefined);
        }
        setIsLoadingPaymentMethods(false);
      })
      .catch(() => {
        setError(t('error_loading_payment_methods'));
        setIsLoadingPaymentMethods(false);
        setPaymentMethods([]);
        setSelectedVariant(undefined);
      });
  }, [t, isLoggedIn]);

  const updateSelectedVariant = useCallback(
    (variantValue) => {
      const newVariant = paymentMethods.find(
        (method) => method.value === variantValue
      );
      setSelectedVariant(newVariant);
      if (newVariant) {
        setCurrency(newVariant.currency);
      }
    },
    [paymentMethods]
  );

  const value = useMemo(
    () => ({
      paymentMethods,
      selectedVariant,
      isLoadingPaymentMethods,
      error,
      currency,
      updateSelectedVariant,
      fetchError: error,
    }),
    [
      paymentMethods,
      selectedVariant,
      isLoadingPaymentMethods,
      error,
      currency,
      updateSelectedVariant,
    ]
  );

  return (
    <BalanceContext.Provider value={value}>{children}</BalanceContext.Provider>
  );
};

export default BalanceContext;
